import React, { useState, useEffect, useRef } from 'react';
import styles from './Search.module.css';

interface SearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const Search: React.FC<SearchProps> = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when search opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // small timeout to allow animation
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Prevent body scroll when search is open (desktop) but allow inner scroll for mobile panel
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => document.body.classList.remove('no-scroll');
  }, [isOpen]);

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      setIsSearching(true);
      // TODO: Replace with real search navigation/logic
      console.log('Searching for:', q);
      setTimeout(() => {
        setIsSearching(false);
        // keep modal open or close depending on desired UX; we'll close
        onClose();
      }, 700);
    }
  };

  const handleTagClick = (tag: string) => {
    setSearchQuery(tag);
    // on mobile we can submit immediately for faster flow
    handleSearch();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop Blur */}
      <div
        className={styles.backdrop}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Search Modal / Bottom sheet */}
      <div className={styles.panel} role="dialog" aria-modal="true" aria-label="Search">
        <div className={styles.panelInner}>
          <button
            className={styles.closeIconBtn}
            onClick={handleClose}
            aria-label="Close search"
            title="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          <form className={styles.form} onSubmit={handleSearch} role="search">
            <label htmlFor="global-search" className={styles.visuallyHidden}>Search</label>

            <div className={styles.inputRow}>
              <div className={styles.iconWrap} aria-hidden>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                  <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>

              <input
                id="global-search"
                ref={inputRef}
                className={styles.input}
                type="search"
                placeholder="Search articles, topics, authors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={isSearching}
                autoComplete="off"
                aria-label="Search articles, topics, authors"
              />

              {searchQuery && (
                <button
                  type="button"
                  className={styles.clear}
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 9L9 15M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                </button>
              )}

              <button
                type="submit"
                className={styles.submit}
                aria-label="Start search"
                disabled={!searchQuery.trim() || isSearching}
              >
                {isSearching ? <span className={styles.spinner} /> : 'Search'}
              </button>
            </div>
          </form>

          <div className={styles.hints}>
            <div className={styles.hintTitle}>Popular</div>
            <div className={styles.hintChips} role="list">
              <button type="button" className={styles.chip} onClick={() => handleTagClick('AI Technology')}>AI Technology</button>
              <button type="button" className={styles.chip} onClick={() => handleTagClick('Startup Funding')}>Startup Funding</button>
              <button type="button" className={styles.chip} onClick={() => handleTagClick('Innovation')}>Innovation</button>
              <button type="button" className={styles.chip} onClick={() => handleTagClick('Web3')}>Web3</button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Search;